import {
  isHaklaiPre1997AcquisitionDateEligible,
  shouldUseHaklaiTestatorBoughtPropertyDate
} from "../preCalc/logic/haklaiPreCalcHelpers";
import { isHaklaiRabinovichEligible } from "../../domain/haklaiPrisaDependencies";
import {
  getRenderablePropertyPartRows,
  isHaklaiPre1997MinimalZabanRelevantForPart
} from "./haklaiFlowHelpers";
import {
  getHaklaiExpenseDepreciationMode,
  hasHaklaiManualForm11DepreciationInput,
  hasHaklaiSelectablePurchaseDepreciationProfile,
  HAKLAI_PROPERTY_DEPRECIATION_MODE,
  isHaklaiManualPurchaseDepreciationAllowed,
  normalizeHaklaiResidentialRentalByPart
} from "./haklaiDepreciationHelpers";
import {
  getHaklaiExpenseDepreciationDecision,
  getHaklaiExpenseTypeDepreciationPolicy,
  HAKLAI_EXPENSE_DEPRECIATION_DECISION,
  isHaklaiExpenseDepreciationPolicyRelevant,
  isHaklaiExpenseDepreciationStateMeaningful,
  normalizeHaklaiExpenseDepreciationDecisionState
} from "../expenses/logic/haklaiExpenseDepreciationPolicy";
import { normalizeHaklaiPurchaseZabanAttributionByPart } from "../expenses/logic/haklaiExpensesHelpers";

const getPlainObject = (value) => (
  value
  && typeof value === "object"
  && !Array.isArray(value)
    ? value
    : {}
);

const getExpenseTargetDepreciationByPart = (expense = {}) => {
  const targetPartKey = expense.targetPartKey;
  const depreciationByPart = getPlainObject(expense.depreciationByPart);
  if (
    !targetPartKey
    || !Object.prototype.hasOwnProperty.call(depreciationByPart, targetPartKey)
  ) {
    return {};
  }
  return { [targetPartKey]: depreciationByPart[targetPartKey] };
};

export const sanitizePre1997ConstructionFlags = (propertyPartsData = {}, preCalc = {}) => {
  let changed = false;
  const hasZaban = preCalc?.hasZaban === true;
  const acquisitionDateEligible = isHaklaiPre1997AcquisitionDateEligible(preCalc);

  const nextParts = Object.entries(propertyPartsData || {}).reduce((acc, [partKey, part]) => {
    if (
      part?.constructionsDoneBeforeApril1997 === true
      && (
        !acquisitionDateEligible
        || !isHaklaiPre1997MinimalZabanRelevantForPart(part, hasZaban)
      )
    ) {
      changed = true;
      acc[partKey] = { ...part, constructionsDoneBeforeApril1997: false };
      return acc;
    }

    acc[partKey] = part;
    return acc;
  }, {});

  return {
    changed,
    propertyPartsData: changed ? nextParts : propertyPartsData
  };
};

const sanitizeExpenseLevelDepreciation = (
  expenses = [],
  propertyPartsData = {},
  residentialRentalByPart = {}
) => {
  const activePartKeys = new Set(
    getRenderablePropertyPartRows(propertyPartsData).map(([partKey]) => partKey)
  );

  return (Array.isArray(expenses) ? expenses : [])
    .filter((expense = {}) => expense.expenseScope !== "globalMeshek")
    .filter((expense = {}) => activePartKeys.has(expense.targetPartKey))
    .map((expense = {}) => {
      const nextExpense = { ...expense };
      delete nextExpense.isDepreciable;
      delete nextExpense.depreciation;
      const policy = getHaklaiExpenseTypeDepreciationPolicy(nextExpense.expenseType || "");
      const nextDepreciationByPart = Object.entries(getExpenseTargetDepreciationByPart(nextExpense))
        .reduce((acc, [partKey, depreciation]) => {
          const part = propertyPartsData?.[partKey];
          const mode = getHaklaiExpenseDepreciationMode(part);
          const decision = getHaklaiExpenseDepreciationDecision(depreciation);
          const hasRequiredProfile = mode === HAKLAI_PROPERTY_DEPRECIATION_MODE.RESIDENTIAL_AUTO
            ? Boolean(residentialRentalByPart?.[partKey])
            : mode === HAKLAI_PROPERTY_DEPRECIATION_MODE.MANUAL_FORM_11;
          const isCompatibleWithMode = (
            (
              decision === HAKLAI_EXPENSE_DEPRECIATION_DECISION.RESIDENTIAL_AUTO
              && mode === HAKLAI_PROPERTY_DEPRECIATION_MODE.RESIDENTIAL_AUTO
            )
            || (
              decision === HAKLAI_EXPENSE_DEPRECIATION_DECISION.FORM_11
              && mode === HAKLAI_PROPERTY_DEPRECIATION_MODE.MANUAL_FORM_11
            )
          );
          if (
            partKey
            && part
            && hasRequiredProfile
            && isHaklaiExpenseDepreciationPolicyRelevant(policy)
            && isCompatibleWithMode
            && isHaklaiExpenseDepreciationStateMeaningful(depreciation)
          ) {
            acc[partKey] = normalizeHaklaiExpenseDepreciationDecisionState(depreciation);
          }
          return acc;
        }, {});
      if (Object.keys(nextDepreciationByPart).length > 0) {
        nextExpense.depreciationByPart = nextDepreciationByPart;
      } else {
        delete nextExpense.depreciationByPart;
      }
      return nextExpense;
    });
};

const sanitizePurchaseDepreciationByPart = (
  purchaseDepreciationByPart = {},
  propertyPartsData = {}
) => {
  const depreciationByPart = (
    purchaseDepreciationByPart
    && typeof purchaseDepreciationByPart === "object"
    && !Array.isArray(purchaseDepreciationByPart)
  ) ? purchaseDepreciationByPart : {};

  return Object.entries(depreciationByPart).reduce((acc, [partKey, depreciation]) => {
    const part = propertyPartsData?.[partKey];
    if (
      !part
      || !isHaklaiManualPurchaseDepreciationAllowed(part)
    ) {
      return acc;
    }
    if (
      depreciation
      && typeof depreciation === "object"
      && !Array.isArray(depreciation)
      && hasHaklaiManualForm11DepreciationInput(depreciation)
    ) {
      acc[partKey] = depreciation;
    }
    return acc;
  }, {});
};

const sanitizeResidentialRentalByPart = (
  residentialRentalByPart = {},
  propertyPartsData = {}
) => normalizeHaklaiResidentialRentalByPart(
  residentialRentalByPart,
  propertyPartsData
);

const sanitizePropertyPartsForRawPayload = (propertyPartsData = {}) => {
  if (!propertyPartsData || typeof propertyPartsData !== "object" || Array.isArray(propertyPartsData)) {
    return {};
  }

  return Object.entries(propertyPartsData).reduce((acc, [partKey, part]) => {
    if (!part || typeof part !== "object" || Array.isArray(part)) {
      acc[partKey] = part;
      return acc;
    }

    const nextPart = { ...part };
    delete nextPart.isRented;
    acc[partKey] = nextPart;
    return acc;
  }, {});
};

const sanitizeDraftExpensesForRawPayload = (expenses = []) => (
  (Array.isArray(expenses) ? expenses : [])
    .filter((expense = {}) => expense.expenseScope !== "globalMeshek")
    .map((expense = {}) => {
      const nextExpense = { ...expense };
      delete nextExpense.isDepreciable;
      delete nextExpense.depreciation;
      const nextDepreciationByPart = getExpenseTargetDepreciationByPart(nextExpense);
      if (Object.keys(nextDepreciationByPart).length > 0) {
        nextExpense.depreciationByPart = nextDepreciationByPart;
      } else {
        delete nextExpense.depreciationByPart;
      }
      return nextExpense;
    })
);

const sanitizePurchaseDepreciationSelectionByPart = ({
  purchaseDepreciationSelectionByPart = {},
  propertyPartsData = {},
  residentialRentalByPart = {},
  purchaseDepreciationByPart = {}
} = {}) => {
  const selectionByPart = (
    purchaseDepreciationSelectionByPart
    && typeof purchaseDepreciationSelectionByPart === "object"
    && !Array.isArray(purchaseDepreciationSelectionByPart)
  ) ? purchaseDepreciationSelectionByPart : {};

  return Object.entries(selectionByPart).reduce((acc, [partKey, isSelected]) => {
    const part = propertyPartsData?.[partKey];
    const hasPurchaseProfile = hasHaklaiSelectablePurchaseDepreciationProfile({
      partKey,
      part,
      residentialRentalByPart,
      purchaseDepreciationByPart
    });
    if (isSelected === true && part && hasPurchaseProfile) {
      acc[partKey] = true;
    }
    return acc;
  }, {});
};

const sanitizeAcquisitionDetailsForWay = (preCalc = {}) => {
  const acquisitionDetails = { ...(preCalc.acquisitionDetails || {}) };
  const clearIfPresent = (fieldName) => {
    if (Object.prototype.hasOwnProperty.call(acquisitionDetails, fieldName)) {
      acquisitionDetails[fieldName] = "";
    }
  };

  if (preCalc.wayOfGettingProperty === "inheritance") {
    clearIfPresent("deduDate");
    if (!shouldUseHaklaiTestatorBoughtPropertyDate(acquisitionDetails.testatorPassingDate)) {
      clearIfPresent("testatorBoughtPropertyDate");
    }
    return acquisitionDetails;
  }

  clearIfPresent("testatorPassingDate");
  clearIfPresent("testatorBoughtPropertyDate");
  return acquisitionDetails;
};

const sanitizePtorTypeForWay = (preCalc = {}) => {
  if (preCalc.ptorType === "single") return "single";
  if (preCalc.ptorType === "inheritance" && preCalc.wayOfGettingProperty === "inheritance") {
    return "inheritance";
  }
  return "none";
};

export const buildHaklaiRawPayload = (sourceCalc = {}) => {
  const calcToSend = JSON.parse(JSON.stringify(sourceCalc || {}));
  calcToSend.mainCalc = calcToSend.mainCalc || {};
  if (calcToSend.preCalc) {
    calcToSend.preCalc.acquisitionDetails = sanitizeAcquisitionDetailsForWay(calcToSend.preCalc);
    calcToSend.preCalc.ptorType = sanitizePtorTypeForWay(calcToSend.preCalc);
    if (!(calcToSend.preCalc.wayOfGettingProperty === "gift" && calcToSend.preCalc.ptorType === "single")) {
      calcToSend.preCalc.hasTzinunOnPtor = false;
    }
    calcToSend.preCalc.acquisitionDetails.hasRabinovich = (
      calcToSend.preCalc.acquisitionDetails.hasRabinovich === true
      && isHaklaiRabinovichEligible(calcToSend.preCalc)
    );
    calcToSend.preCalc.parcelLookupError = null;
    delete calcToSend.preCalc.cosnstructionsDoneBeforeApril1997;
  }
  calcToSend.mainCalc.propertyPartsData = sanitizePropertyPartsForRawPayload(
    calcToSend.mainCalc.propertyPartsData || {}
  );
  const sanitizedPre1997State = sanitizePre1997ConstructionFlags(
    calcToSend.mainCalc.propertyPartsData || {},
    calcToSend.preCalc || {}
  );
  if (sanitizedPre1997State.changed) {
    calcToSend.mainCalc.propertyPartsData = sanitizedPre1997State.propertyPartsData;
  }
  calcToSend.mainCalc.residentialRentalByPart = sanitizeResidentialRentalByPart(
    calcToSend.mainCalc.residentialRentalByPart,
    calcToSend.mainCalc.propertyPartsData || {}
  );
  calcToSend.mainCalc.purchaseDepreciationByPart = sanitizePurchaseDepreciationByPart(
    calcToSend.mainCalc.purchaseDepreciationByPart,
    calcToSend.mainCalc.propertyPartsData || {}
  );
  calcToSend.mainCalc.purchaseDepreciationSelectionByPart = sanitizePurchaseDepreciationSelectionByPart({
    purchaseDepreciationSelectionByPart: calcToSend.mainCalc.purchaseDepreciationSelectionByPart,
    propertyPartsData: calcToSend.mainCalc.propertyPartsData || {},
    residentialRentalByPart: calcToSend.mainCalc.residentialRentalByPart,
    purchaseDepreciationByPart: calcToSend.mainCalc.purchaseDepreciationByPart
  });
  calcToSend.mainCalc.purchaseZabanAttributionByPart = normalizeHaklaiPurchaseZabanAttributionByPart(
    calcToSend.mainCalc.purchaseZabanAttributionByPart,
    calcToSend.mainCalc.propertyPartsData || {},
    calcToSend.preCalc?.hasZaban === true
  );
  if (Array.isArray(calcToSend.mainCalc.expenses)) {
    calcToSend.mainCalc.expenses = sanitizeExpenseLevelDepreciation(
      calcToSend.mainCalc.expenses,
      calcToSend.mainCalc.propertyPartsData || {},
      calcToSend.mainCalc.residentialRentalByPart
    );
  }
  if (!calcToSend.mainCalc.prisa?.shouldTryPrisa) {
    calcToSend.mainCalc.prisa = { shouldTryPrisa: false, personsData: [] };
  }
  return calcToSend;
};

export const buildHaklaiDraftRawPayload = (sourceCalc = {}) => {
  const calcToSend = JSON.parse(JSON.stringify(sourceCalc || {}));
  calcToSend.mainCalc = calcToSend.mainCalc || {};
  if (calcToSend.preCalc) {
    calcToSend.preCalc.parcelLookupError = null;
    delete calcToSend.preCalc.cosnstructionsDoneBeforeApril1997;
  }
  calcToSend.mainCalc.propertyPartsData = sanitizePropertyPartsForRawPayload(
    calcToSend.mainCalc.propertyPartsData || {}
  );
  if (Array.isArray(calcToSend.mainCalc.expenses)) {
    calcToSend.mainCalc.expenses = sanitizeDraftExpensesForRawPayload(calcToSend.mainCalc.expenses);
  }
  if (!calcToSend.mainCalc.prisa?.shouldTryPrisa) {
    calcToSend.mainCalc.prisa = { shouldTryPrisa: false, personsData: [] };
  }
  return calcToSend;
};
